import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { KAL_DIALOG_DATA, KalDialogConfig, KalDialogRef } from '@kalidea/kaligraphi';
import { forkJoin, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    ComplementarySubventionInfo,
    SharedAdvancedRuleEditDialogComponent
} from 'src/app/_shared/dialogs/shared-advanced-rules/shared-advanced-rule-edit-dialog/shared-advanced-rule-edit-dialog.component';
import { AdvancedRule, AdvancedRuleService } from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import {
    SharedAllocationExecutionCode
} from 'src/app/_shared/stores/shared-allocation-execution/shared-allocation-execution.actions';
import { TypeActivite } from 'src/app/board/activity/activity.constants';
import { TypesRegleEnum } from 'src/graphql/generated-types';

export interface SharedAdvancedRuleDetailDialogData {
    rule?: AdvancedRule;
    ruleLabel: string;
    id: number;
    isInteractivity?: boolean;
    isPlanning?: boolean;
    activityType?: TypeActivite;
    ruleType: TypesRegleEnum;
    complementarySubvention?: ComplementarySubventionInfo;
    context?: SharedAllocationExecutionCode;
}

@Component({
    selector: 'app-shared-advanced-rule-detail-dialog',
    templateUrl: './shared-advanced-rule-detail-dialog.component.html',
    styleUrls: ['./shared-advanced-rule-detail-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAdvancedRuleDetailDialogComponent extends BaseComponent implements OnInit {

    rule: AdvancedRule = null;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly dialogRef: KalDialogRef<SharedAdvancedRuleDetailDialogComponent>,
                private readonly advancedRuleService: AdvancedRuleService,
                @Inject(KAL_DIALOG_DATA) public readonly data: SharedAdvancedRuleDetailDialogData) {
        super(cdr);
    }

    get ruleType(): TypesRegleEnum {
        return this.data.ruleType;
    }

    get isNotAccessibilityRule(): boolean {
        return this.data.ruleType !== TypesRegleEnum.Accessibilite;
    }

    static config(data: SharedAdvancedRuleDetailDialogData): KalDialogConfig<SharedAdvancedRuleDetailDialogData> {

        const {ruleType, ruleLabel} = data;

        return {
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            maxWidth: '800px',
            maxHeight: '100vh',
            title: `Détail de la règle ${SharedAdvancedRuleEditDialogComponent.rulesTypesLabel[ruleType]} `,
            data
        };
    }

    close(): void {
        this.dialogRef.close();
    }

    ngOnInit(): void {
        const {ruleType, isInteractivity = false, id, rule, complementarySubvention} = this.data;

        const getRule = !rule ? this.advancedRuleService.getAdvancedRule$(id, ruleType) : of({Regle: rule});

        this.watch$ = this.wrapLoader$(forkJoin({
            relatedLists: this.advancedRuleService.loadRelatedLists$(ruleType),
            complementaryEnvelopeList: ruleType === TypesRegleEnum.Subvention && !isInteractivity ? this.advancedRuleService.getComplementaryEnvelopeList() : of(null),
            roundingTypesList: this.isNotAccessibilityRule ? this.advancedRuleService.getRoundingTypesList$() : of([]),
            enveloppeId: !!complementarySubvention?.IsSubventionEnveloppeComplementaire ? this.advancedRuleService.getComplementaryRuleEnvelopeId$(id || rule.Id) : of(null),
        }).pipe(
            tap(({enveloppeId}) => {
                if (!!complementarySubvention?.IsSubventionEnveloppeComplementaire) {
                    complementarySubvention.IdEnveloppeComplementaire = enveloppeId;
                }
            }),
            switchMap(() => getRule),
            tap(rule => this.rule = rule.Regle)
        ), 'init');
    }

}
